<template>
    <div>

        <kgr-form-scaffold
            :pending="false"
            :with-back-button="false"
            :save-button-disabled="saveButtonDisabled"
            :save-button-label="certificateType === 'tax' ? $t('Steuerbescheinigung erstellen') : $t('Beitragsbescheinigung erstellen')"
            @primaryBtnClicked="create()"
        >
            <kgr-note-box text-key="childCertificates.note" /><br />
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <div class="kgr-inputGroup">
                        <kgr-radio
                            v-model="certificateType"
                            :options="certificateTypeOptions"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-parent-user-select
                            v-model="recipient"
                            :child-id="childId"
                            @post-input="loadCertificates"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-month-picker
                            v-if="certificateType !== 'tax'"
                            :month="startYearOfMonth.month"
                            :year="startYearOfMonth.year"
                            :label="$t('Leistungszeitraum von')"
                            @monthOfYear="startYearOfMonth = $event"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-month-picker
                            v-if="certificateType !== 'tax'"
                            :month="endYearOfMonth.month"
                            :year="endYearOfMonth.year"
                            :start="minYearOfMonth"
                            :label="$t('bis')"
                            @monthOfYear="endYearOfMonth = $event"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-dropdown
                            v-if="certificateType === 'tax'"
                            v-model="year"
                            :options="years"
                            display="label"
                            :translate-values="false"
                            :label="$t('Jahr')"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-checkbox
                            v-model="paidOnly"
                            :label="$t('Nur bezahlte Rechnungen berücksichtigen')"
                        />
                    </div>
                    <div class="kgr-inputGroup">
                        <kgr-checkbox
                            v-if="certificateType === 'tax'"
                            v-model="separateFoodAndOtherCosts"
                            :label="$t('Verpflegungskosten und Sonstige Gebühren getrennt aufführen')"
                        />
                    </div>
                </div>
                <div class="kgr-grid__cell">
                    <div class="kgr-grid__cell">

                        <div
                            v-for="costType in costTypes"
                            :key="costType.label"
                            class="kgr-inputGroup"
                        >
                            <div class="kgr-inputGroup__label required">{{ costType.label }}</div>
                            <div class="kgr-inputGroup__text"></div>
                            <div id="tax_cert_filter_costTypes">
                                <label
                                    v-for="option in costType.options"
                                    :key="option.id"
                                    class="kgr-checkbox kgr-row ui-droppable"
                                >
                                    <kgr-checkbox
                                        v-model="option.checked"
                                        :value="option.label"
                                        :label="option.label"
                                        :name="'tax_cert_filter[' + costType.label + '][]'"
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </kgr-form-scaffold>
        <br />
        <br />
        <kgr-child-certificates-list
            :certificates="certificates"
            :child-id="childId"
            :recipient-identifier="recipient ? recipient.identifier : null"
            :loading="loading"
            @change="loadCertificates"
        />
    </div>
</template>

<script>
import CertificateRepository from '../../../js/repository/CertificateRepository.js';
import Checkbox from '../../common/form/checkbox/Checkbox.vue';
import ChildCertificatesList from './ChildCertificatesList.vue';
import Dropdown from '../../common/form/select/Dropdown.vue';
import FormScaffold from '../../common/form/FormScaffold.vue';
import GenericRadio from '../../common/form/radio/GenericRadio.vue';
import MonthOfYear from '../../../js/date/MonthOfYear';
import MonthPicker from '../../common/form/picker/MonthPicker.vue';
import NoteBox from '../../common/NoteBox.vue';
import ParentUserSelect from '../../common/form/select/ParentUserSelect.vue';

export default {
    components: {
        'kgr-month-picker': MonthPicker,
        'kgr-parent-user-select': ParentUserSelect,
        'kgr-checkbox': Checkbox,
        'kgr-form-scaffold': FormScaffold,
        'kgr-note-box': NoteBox,
        'kgr-radio': GenericRadio,
        'kgr-dropdown': Dropdown,
        'kgr-child-certificates-list': ChildCertificatesList,
    },
    props: {
        childId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            year: {label: new Date().getFullYear(), value: new Date().getFullYear()},
            // "years" is an array of years from 2000 to the current year
            years:  Array.from({ length: new Date().getFullYear() - 2000 + 1 }, (v, k) => {return {label: k + 2000, value: k + 2000}}),
            minYearOfMonth: new MonthOfYear(1, 2000),
            startYearOfMonth: new MonthOfYear(1,new Date().getFullYear()),
            endYearOfMonth: new MonthOfYear(12, new Date().getFullYear()),
            loading: false,
            recipient: null,
            paidOnly: false,
            separateFoodAndOtherCosts: false,
            certificateType: 'tax',
            certificateTypeOptions: {
                'childCertificates.switch.tax': 'tax',
                'childCertificates.switch.cost.overview': 'cost.overview',
                'childCertificates.switch.cost.details': 'cost.details',
            },
            certificates: [],
            costTypes: [
                {
                    label: this.$t('cost.category.care'),
                    options: [
                        { id: 'ChildCostCareType', label: this.$t('cost.category.care_type.long'), checked: true },
                        { id: 'ChildCareCostMonthly', label: this.$t('cost.category.care_monthly'), checked: true },
                        { id: 'ChildCareCostDaily', label: this.$t('cost.category.care_daily'), checked: true },
                        { id: 'ChildCareCostHourly', label: this.$t('cost.category.care_hourly'), checked: true },
                    ]
                },
                {
                    label: this.$t('cost.category.food'),
                    options: [
                        { id: 'ChildFoodCostMonthly', label: this.$t('cost.category.food_monthly'), checked: true },
                        { id: 'ChildFoodCostDaily', label: this.$t('cost.category.food_daily'), checked: true },
                        { id: 'ChildFoodCostSingle', label: this.$t('cost.category.food_single'), checked: true },
                    ]
                },
                {
                    label: this.$t('cost.category.otherAndSubventions'),
                    options: [
                        { id: 'Subvention', label: this.$t('cost.category.subvention_by_state'), checked: true },
                        { id: 'CostUnitSubventionForCare', label: this.$t('cost.category.subvention_for_care'), checked: true },
                        { id: 'CostUnitSubventionNonCare', label: this.$t('cost.category.subvention_for_non_care'), checked: true },
                        { id: 'ChildOtherCostMonthly', label: this.$t('cost.category.other_monthly'), checked: true },
                        { id: 'ChildOtherCostDaily', label: this.$t('cost.category.other_daily'), checked: true },
                        { id: 'ChildOtherCostSingle', label: this.$t('cost.category.other_single'), checked: true },
                    ]
                }
            ]
        }
    },
    computed: {
        selectedCostTypes() {
            return this.costTypes.flatMap(type => type.options.filter(option => option.checked).map(option => option.id));
        },
        saveButtonDisabled() {
            return !this.recipient || this.selectedCostTypes.length === 0 || this.startYearOfMonth.isAfter(this.endYearOfMonth);
        },
    },
    methods: {
        create() {
            switch (this.certificateType) {
                case 'tax':
                    CertificateRepository.createTaxCertificate(this.childId, this.recipient, this.selectedCostTypes, this.year.value, this.paidOnly, this.separateFoodAndOtherCosts)
                        .then(() => {
                            this.$notify.success('saveChanges.success');
                            this.loadCertificates();
                        })
                        .catch(error => {
                            this.$notify.error(error.message ?? 'saveChanges.error');
                        });
                    break;
                case 'cost.overview':
                case 'cost.details':
                    CertificateRepository.createCostCertificate(this.childId, this.recipient, this.certificateType === 'cost.details', this.selectedCostTypes, this.startYearOfMonth, this.endYearOfMonth, this.paidOnly)
                        .then(() => {
                            this.$notify.success('saveChanges.success');
                            this.loadCertificates();
                        })
                        .catch(error => {
                            this.$notify.error(error.message ?? 'saveChanges.error');
                        });
                    break;
                default:
                    console.error('Unknown certificate type:', this.certificateType);
            }
        },
        loadCertificates() {
            if (!this.recipient) {
                return;
            }
            this.loading = true;
            CertificateRepository.findByChildAndInvoiceRecipient(this.childId, this.recipient.identifier)
                .then(response => {
                    this.certificates = response;
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
